import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/da5g15oet96csuseq3d0';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function ContactPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: t('contact.title'),
    description: t('contact.subtitle'),
    url: `${import.meta.env.VITE_SITE_URL}/contact`,
  });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    setStatus('sending');
    setError('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        params.append(key, value);
      }
    });

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setError(
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || t('contact.error'),
        );
      }
    } catch {
      setStatus('error');
      setError(t('contact.error'));
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {t('contact.title')}
          </h1>
          <p className="mt-3 text-sm text-foreground-600 md:text-base">{t('contact.subtitle')}</p>
        </div>

        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="mx-auto mt-8 max-w-xl rounded-lg border border-background-200/70 bg-background-50 p-6"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                {t('contact.name')}
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                {t('contact.email')}
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                {t('contact.message')}
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                maxLength={500}
                className="w-full resize-y rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
              />
            </div>

            {/* Anti-spam honeypot field */}
            <input
              type="text"
              name="website_alt"
              className="hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {status === 'sending' ? t('contact.sending') : t('contact.submit')}
            </button>
          </div>

          {status === 'success' && (
            <p className="mt-4 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
              {t('contact.success')}
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
              {error || t('contact.error')}
            </p>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
}